/**
 * @file 自适应iframe内容高度的组件
 * @author alfielv
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Frame } from '@tencent/odc';
import { Loading } from '@tencent/spaui';

/**
 * 在 HTML 字符串的 </body> 标签前插入高度监听脚本
 * @param htmlString 原始 HTML 字符串
 * @param origin 父页面域名（用于安全验证）
 * @returns 修改后的 HTML 字符串
 */
function injectResizeScript(htmlString: string, origin: string): string {
  // 关键正则：匹配闭合的 </body> 标签（兼容大小写、空格、换行）
  const bodyCloseRegex = /(<\/body\s*>)/i;

  // 构造待插入的脚本（动态注入 origin 防止 XSS）
  const scriptContent = `
    <script>
      document.addEventListener("DOMContentLoaded", () => {
        // 监听所有 Web Components 的尺寸变化,只要有一个发生变化，就postMessage
        const components = Array.prototype.slice.call(document.querySelectorAll('*')).filter(el => el.shadowRoot);
        const resizeObserver = new ResizeObserver(entries => {
          const height = document.body.scrollHeight;
          window.parent.postMessage({ type: 'resize', height }, '${origin}');
        });

        components.forEach(comp => {
          if (comp.shadowRoot) {
            // 监听 Shadow Root 内首个实际渲染元素(非style元素)
            const firstChild = Array.prototype.slice.call(comp.shadowRoot.querySelectorAll('*')).find(el => el.nodeName !== 'STYLE');
            if (firstChild) {
              resizeObserver.observe(firstChild);
            }
          }
        });
      });

      // 初始加载时发送高度
      window.addEventListener('load', () => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: 'resize', height }, '${origin}');
      });
    </script>
  `;

  // 在 </body> 前插入脚本
  if (bodyCloseRegex.test(htmlString)) {
    return htmlString.replace(
      bodyCloseRegex,
      `${scriptContent}$1`,
    );
  }
  // 兼容无 </body> 标签的异常情况
  return `${htmlString}${scriptContent}`;
}


interface ResponsiveIframeProps {
  /**
   * iframe内容
   */
  srcDoc: string;
  /**
   * iframe加载完成时回调
   * @returns
   */
  onLoad?: () => void;
}

const ResponsiveIframe = ({ srcDoc, onLoad }: ResponsiveIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const [isReady, setIsReady] = React.useState(false);

  // 调整iframe高度的核心函数
  const adjustHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;

    try {
      // 优先获取完整文档高度
      const contentHeight = iframe.contentDocument.body.scrollHeight;
      iframe.style.height = `${contentHeight}px`;
    } catch (error) {
      console.error('跨域限制无法直接访问DOM，启用postMessage通信模式');
    }
  }, []);

  // 初始化MutationObserver
  const initObserver = useCallback(() => {
    if (!iframeRef.current) {
      return;
    }
    const iframeDoc = iframeRef.current.contentDocument;
    if (!iframeDoc) {
      return;
    }

    // 清理旧Observer避免重复监听
    if (observerRef.current) {
      observerRef.current.disconnect();
    };

    observerRef.current = new MutationObserver(() => {
      adjustHeight();
    });

    // 配置监听范围（包括属性/子节点/后代节点变化）
    observerRef.current.observe(iframeDoc.body, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
  }, [adjustHeight]);

  // 主副作用逻辑
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // iframe加载完成后初始化
    const onLoad = () => {
      try {
        initObserver();
        adjustHeight();
      } catch (e) {
        console.error('iframe自适应失败', e);
      }
    };
    iframe.addEventListener('load', onLoad);

    // 清理函数
    return () => {
      iframe.removeEventListener('load', onLoad);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [adjustHeight, initObserver]);

  // 接收子页面高度消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 安全验证：仅接受来自当前 iframe 域的消息
      if (event.origin !== window.origin || !iframeRef.current) return;
      if (event.data?.type === 'resize') {
        iframeRef.current.style.height = `${event.data.height}px`;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <Frame w="100%" position='relative'>
      {!isReady && <Loading className="tw-absolute tw-w-full tw-h-[24px] tw-left-0 tw-top-0" />}
      <iframe
        ref={iframeRef}
        srcDoc={injectResizeScript(srcDoc, window.location.origin)}
        width="100%"
        height="100%"
        onLoad={() => {
          setIsReady(true);
          onLoad?.();
        }}
      />
    </Frame>
  );
};

export default ResponsiveIframe;
